type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

interface ColonyAvatarProps {
  src?: string;
  name: string;
  size?: AvatarSize;
  status?: "online" | "offline" | "busy" | "away";
  className?: string;
}

const sizeMap: Record<AvatarSize, { container: string; text: string; status: string }> = {
  xs: { container: "w-5 h-5", text: "text-[9px]", status: "w-2 h-2 border" },
  sm: { container: "w-6 h-6", text: "text-[10px]", status: "w-2.5 h-2.5 border" },
  md: { container: "w-8 h-8", text: "text-[11px]", status: "w-3 h-3 border-2" },
  lg: { container: "w-10 h-10", text: "text-[13px]", status: "w-3.5 h-3.5 border-2" },
  xl: { container: "w-12 h-12", text: "text-[15px]", status: "w-4 h-4 border-2" },
};

const STATUS_BG: Record<string, string> = {
  online: "#22C55E",
  offline: "#9CA3AF",
  busy: "#EF4444",
  away: "#F59E0B",
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLORS = [
  { bg: "rgba(59,130,246,0.2)", text: "#3B82F6" },
  { bg: "rgba(139,92,246,0.2)", text: "#8B5CF6" },
  { bg: "rgba(6,182,212,0.2)", text: "#06B6D4" },
  { bg: "rgba(34,197,94,0.2)", text: "#22C55E" },
  { bg: "rgba(245,158,11,0.2)", text: "#F59E0B" },
  { bg: "rgba(239,68,68,0.2)", text: "#EF4444" },
];

function getColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function ColonyAvatar({
  src,
  name,
  size = "md",
  status,
  className = "",
}: ColonyAvatarProps) {
  const s = sizeMap[size];
  const color = getColor(name);

  return (
    <div className={`relative inline-flex shrink-0 ${className}`}>
      {src ? (
        <img
          src={src}
          alt={name}
          className={`${s.container} rounded-full object-cover`}
        />
      ) : (
        <div
          className={`${s.container} rounded-full flex items-center justify-center font-medium select-none ${s.text}`}
          style={{ backgroundColor: color.bg, color: color.text }}
        >
          {getInitials(name)}
        </div>
      )}
      {status && (
        <span
          className={`absolute -bottom-0.5 -right-0.5 rounded-full ${s.status}`}
          style={{ backgroundColor: STATUS_BG[status], borderColor: "var(--colony-bg)" }}
        />
      )}
    </div>
  );
}
